import {
  BOT_SEQUENCE_VAR_KEYS,
  BOT_SEQUENCE_VAR_LABELS,
  BotSequenceVarKey,
  LOCATION_URL_ENTRIES,
  VarDef,
  VarDefinition,
  VarKey,
  VarType
} from "@swivl-mono/types";

/**
 * `{{Type:Key}}` variables — the Snowy-era variable syntax (ENG-3109).
 *
 * Tokens reference the existing VarDef catalog by `VarKey` (e.g. `{{Location:Name}}`),
 * so the display names, descriptions and defaults every variable already has are the
 * single source of truth. This module only PARSES and SUBSTITUTES; the values themselves
 * come from the existing resolver cascade (`VarsService` / `API.Vars.getVarsById`).
 *
 * Deliberately separate from `BotPromptService.injectVariables` (`{{camelCase}}`,
 * bot-prompt-internal): a `Type:Key` token always contains `:`, which `\w+` never
 * matches, so the two namespaces cannot collide.
 *
 * Spec: docs/specs/conversation-starters.md ("The variable system").
 */

/**
 * Matches one `{{Type:Key}}` or `{{Type:Key|"fallback"}}` token; group 1 is the bare
 * `Type:Key`, group 2 (optional, ENG-3216) the double-quoted inline fallback with `\"` and
 * `\\` escapes. Whitespace inside the braces is tolerated.
 */
export const BRACE_VAR_RE = /\{\{\s*([A-Za-z]+:[A-Za-z0-9_-]+)\s*(?:\|\s*"((?:[^"\\]|\\.)*)"\s*)?\}\}/g;

/** A variable as shown to a person: the stored `id` (a `VarKey`) and its human display label. */
export interface BraceVarTag {
  id: string;
  display: string;
}

/** One reference as written: the key and, when the author gave one, its inline fallback (`null` = required). */
export interface BraceVarRef {
  key: string;
  fallback: string | null;
}

export interface ResolveBraceVarsResult {
  /** The input with every resolvable token substituted. Unresolvable tokens are left untouched. */
  text: string;
  /** Referenced keys that had no usable value (undefined, null or blank) AND no inline fallback, in first-seen order. */
  missing: string[];
}

function unescapeFallback(raw: string): string {
  return raw.replace(/\\(.)/g, "$1");
}

function escapeFallback(literal: string): string {
  return literal.replace(/[\\"]/g, (char) => `\\${char}`);
}

/** The stored form of a variable reference; with `fallback`, the optional form `{{Type:Key|"literal"}}`. */
export function braceVarToken(id: string, fallback?: string): string {
  return fallback === undefined ? `{{${id}}}` : `{{${id}|"${escapeFallback(fallback)}"}}`;
}

/** Every reference in `text` with its fallback, in order of appearance (duplicates kept). */
export function extractBraceVarRefs(text: string): BraceVarRef[] {
  const refs: BraceVarRef[] = [];
  for (const match of text.matchAll(BRACE_VAR_RE)) {
    refs.push({ key: match[1], fallback: match[2] === undefined ? null : unescapeFallback(match[2]) });
  }
  return refs;
}

/**
 * Brace pairs left over once every well-formed token is removed — an unbalanced or
 * malformed variable (`{{User:FirstName`, `{{ not a key }}`, `{{Lease:Balance|n/a}}`). Each
 * error names the offending fragment so an editor can point at it.
 */
export function findBraceVarSyntaxErrors(text: string): string[] {
  const stripped = text.replace(BRACE_VAR_RE, "");
  const errors: string[] = [];
  for (const match of stripped.matchAll(/\{\{[^}]{0,60}\}?\}?|\}\}/g)) {
    errors.push(`malformed variable near "${match[0].trim()}" — use {{Type:Key}} or {{Type:Key|"fallback"}}`);
  }
  return errors;
}

/** True when `id` is a key in the VarDef catalog. */
export function isKnownVarKey(id: string): id is VarKey {
  return Object.prototype.hasOwnProperty.call(VarDef, id);
}

/** Every distinct `Type:Key` referenced in `text`, in first-seen order. */
export function extractBraceVarKeys(text: string): string[] {
  const seen = new Set<string>();
  for (const match of text.matchAll(BRACE_VAR_RE)) {
    seen.add(match[1]);
  }
  return [...seen];
}

export function hasBraceVars(text: string): boolean {
  return new RegExp(BRACE_VAR_RE.source).test(text);
}

/**
 * Substitute every token that has a usable value. A token whose value is undefined, null or
 * blank uses its inline fallback when the author gave one (`{{Key|"Customer"}}`); otherwise
 * it is reported in `missing` and left in place — never replaced with an empty string —
 * so the caller can apply the product rule (campaigns skip the recipient; welcome messages
 * fall back to the normal greeting; sequence sends fail) instead of sending a broken message.
 */
export function resolveBraceVars(text: string, values: Record<string, string | null | undefined>): ResolveBraceVarsResult {
  const missing: string[] = [];
  const resolved = text.replace(BRACE_VAR_RE, (token: string, id: string, fallback: string | undefined) => {
    const value = values[id];
    if (value === undefined || value === null || value.trim() === "") {
      if (fallback !== undefined) return unescapeFallback(fallback);
      if (!missing.includes(id)) missing.push(id);
      return token;
    }
    return value;
  });
  return { text: resolved, missing };
}

/**
 * Human label for a variable id — `"Type - Display Name"`, matching the existing studio convention;
 * the id itself when unknown. Link variables have no VarDef entry, so the registry answers for them
 * (otherwise a UI shows the raw `Location:CortexLinkSpotReview`).
 */
export function braceVarDisplayName(id: string): string {
  const link = LOCATION_URL_ENTRIES.find((entry) => entry.variableKey === id);
  if (link) return `Location - ${link.label}`;
  if (isSequenceVarKey(id)) return `Sequence - ${BOT_SEQUENCE_VAR_LABELS[id]}`;
  if (!isKnownVarKey(id)) return id;
  const def: VarDefinition | undefined = VarDef[id];
  return def ? `${def.type} - ${def.displayName}` : id;
}

/** The full VarDef catalog as typeahead options, optionally filtered. */
export function braceVarCatalog(filter?: (def: VarDefinition) => boolean): BraceVarTag[] {
  const defs = Object.values(VarDef) as VarDefinition[];
  return defs.filter((def) => (filter ? filter(def) : true)).map((def) => ({ id: def.varKey, display: `${def.type} - ${def.displayName}` }));
}

/**
 * The link variables (`{{Location:SpotReviewURL}}` and friends) declared by the location URL
 * registry (ENG-3138). They have no VarDef entry because nothing stores them — each is built on
 * demand from whatever the org has configured — so they are listed from the registry instead.
 */
export function locationUrlVarTags(): BraceVarTag[] {
  return LOCATION_URL_ENTRIES.filter((entry) => entry.customerFacing).map((entry) => ({
    id: entry.variableKey,
    display: `Location - ${entry.label}`
  }));
}

/**
 * Every variable a Snowy surface can offer: the stored catalog plus the links. Use this rather
 * than `braceVarCatalog()` anywhere a person is writing text that gets sent — otherwise the links
 * resolve correctly at send time but cannot be typed in the first place.
 */
/**
 * Legacy (non-Cortex) Location/Group/Org vars never appear in a Snowy picker (Chuck, 2026-09-03:
 * a campaign shipped with `{{Group:WebsiteURL}}` because the typeahead offered it). Snowy resolves
 * only Cortex configuration; User/Lease/Tenant/Unit vars are data, not configuration, and stay.
 * This filters what can be PICKED — text already saved with a legacy token still resolves and
 * still displays its human name.
 */
function isSnowyOfferable(def: VarDefinition): boolean {
  const scoped = def.type === VarType.Location || def.type === VarType.Group || def.type === VarType.Org;
  return !scoped || String(def.key).startsWith("Cortex");
}

export function snowyVarCatalog(filter?: (def: VarDefinition) => boolean): BraceVarTag[] {
  return [...braceVarCatalog((def) => isSnowyOfferable(def) && (filter ? filter(def) : true)), ...locationUrlVarTags()];
}

/** True when `id` is a fire-time sequence variable (`Sequence:*`, ENG-3216) — supplied by the engine, not stored. */
export function isSequenceVarKey(id: string): id is BotSequenceVarKey {
  return (BOT_SEQUENCE_VAR_KEYS as readonly string[]).includes(id);
}

/** The fire-time sequence variables as typeahead options — offered only where a sequence text is being authored. */
export function sequenceVarTags(): BraceVarTag[] {
  return BOT_SEQUENCE_VAR_KEYS.map((id) => ({ id, display: `Sequence - ${BOT_SEQUENCE_VAR_LABELS[id]}` }));
}

/** True when `id` resolves somewhere: the VarDef catalog, a location link variable, or a fire-time sequence variable. */
export function isResolvableVarKey(id: string): boolean {
  return isKnownVarKey(id) || LOCATION_URL_ENTRIES.some((entry) => entry.variableKey === id) || isSequenceVarKey(id);
}
